import { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import { FormError } from '../../../types/FormError';
import { LoadState } from '../../../types/LoadState';
import * as varderingClient from '../../clients/VarderingClient';
import { SavedVarderingarTable } from './SavedVarderingarTable';
import { BostadsvarderingDto } from '../../types/dtos/VarderingarModel';

interface SavedVarderingarProps {
  activeBostadsvardering: BostadsvarderingDto;
  setActiveBostadsvardering: (value: BostadsvarderingDto) => void;
  savedVarderingar: BostadsvarderingDto[];
  setSavedVarderingar: (value: BostadsvarderingDto[]) => void;
  loadState: LoadState;
  error: FormError;
  setError: (value: FormError) => void;
}

export const SavedVarderingar: React.FC<SavedVarderingarProps> = ({
  setActiveBostadsvardering,
  activeBostadsvardering,
  savedVarderingar,
  setSavedVarderingar,
  loadState,
  error,
  setError,
}) => {
  const [selected, setSelected] = useState<BostadsvarderingDto[]>([]);
  const [deleteLoadState, setDeleteLoadState] = useState(LoadState.Idle);

  const handleDelete = async () => {
    setError(undefined);
    setDeleteLoadState(LoadState.Loading);
    const response = await varderingClient.deleteVarderings(
      selected.map((s) => s.id)
    );
    if (!response.success) {
      setError({
        severity: 'error',
        text: 'Borttagning av värdering misslyckades.',
      });
      return setDeleteLoadState(LoadState.Error);
    }
    setSelected([]);
    setSavedVarderingar(response.data);
    setDeleteLoadState(LoadState.Success);
  };

  const toggleRowSelect = (bostadsvardering: BostadsvarderingDto) =>
    setSelected(
      selected.find((b) => b.id === bostadsvardering.id)
        ? selected.filter((t) => t.id !== bostadsvardering.id)
        : [...selected, bostadsvardering]
    );

  const toggleSelectAll = (checked: boolean) =>
    savedVarderingar && setSelected(checked ? savedVarderingar : []);

  if (loadState === LoadState.Loading) {
    return (
      <Stack alignItems="center" sx={{ height: 200, pt: 5, pb: 5 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return <Alert severity={error.severity}>{error.text}</Alert>;
  }

  return savedVarderingar.length ? (
    <Stack divider={<Divider />}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center">
        {deleteLoadState === LoadState.Loading ? (
          <Stack
            sx={{ height: '40px', paddingRight: '8px' }}
            justifyContent="center"
          >
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <IconButton
            color="primary"
            disabled={selected.length === 0}
            onClick={handleDelete}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>
      <SavedVarderingarTable
        savedVarderingar={savedVarderingar}
        handleSelectAll={toggleSelectAll}
        selected={selected}
        setActiveBostadsvardering={setActiveBostadsvardering}
        activeBostadsvardering={activeBostadsvardering}
        toggleRowSelect={toggleRowSelect}
      />
    </Stack>
  ) : (
    <Alert severity="info" sx={{ mt: 2 }}>
      Du saknar sparade värderingar.
    </Alert>
  );
};
